import { useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { usePreferences } from './PreferencesContext';

/**
 * useIdleTimeout — auto-logout hook that watches user activity
 * (mousemove/keydown/click/scroll/touchstart) and calls MSAL
 * logoutRedirect after N minutes of inactivity.
 *
 * Reads the preference from localStorage key `pmd-security.sessionTimeout`
 * (set by PreferencesPage — values: '15' | '30' | '60' | '240' | 'never').
 *
 * Non-SysAdmin: capped at 120 min (2h). Preferences above the cap or
 * 'never' clamp down. Rationale from the pen test remediation plan —
 * long-lived sessions on operator accounts are a real risk.
 *
 * SysAdmin: uncapped. 'never' is respected literally.
 *
 * Mount once at the App level (inside AuthProvider so it can read
 * isSysAdmin). No-op when MSAL isn't configured (local dev).
 */

const MAX_MINUTES_NON_SYSADMIN = 120;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
const STORAGE_KEY = 'pmd-security.sessionTimeout';

function resolveTimeoutMinutes(isSysAdmin, prefValue) {
  // Server-backed preference (security.sessionTimeout) is the authority;
  // the localStorage mirror covers the pre-hydration window. The clamp below
  // stays as defense-in-depth regardless of what the server hands us.
  const raw = prefValue || localStorage.getItem(STORAGE_KEY) || 'never';

  if (raw === 'never') {
    return isSysAdmin ? null : MAX_MINUTES_NON_SYSADMIN;
  }

  const asNumber = parseInt(raw, 10);
  if (Number.isNaN(asNumber) || asNumber <= 0) {
    return isSysAdmin ? null : MAX_MINUTES_NON_SYSADMIN;
  }

  if (!isSysAdmin && asNumber > MAX_MINUTES_NON_SYSADMIN) {
    return MAX_MINUTES_NON_SYSADMIN;
  }
  return asNumber;
}

export function useIdleTimeout(msalInstance) {
  const { isSysAdmin, currentUser } = useAuth();
  const { hydrated, getPref } = usePreferences();
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    // No user context (unauthenticated, or local dev with placeholder Entra) → no-op
    if (!currentUser) return undefined;

    const minutes = resolveTimeoutMinutes(isSysAdmin, hydrated ? getPref('security.sessionTimeout') : null);
    if (minutes == null) return undefined;   // uncapped for SysAdmin who chose 'never'

    const timeoutMs = minutes * 60 * 1000;

    const fireLogout = () => {
      // Guard: only trigger if we truly haven't seen activity recently.
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor < timeoutMs) {
        // Activity happened after timer was scheduled — reschedule instead of logging out.
        scheduleNext(timeoutMs - idleFor);
        return;
      }
      try {
        if (msalInstance && typeof msalInstance.logoutRedirect === 'function') {
          msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
        }
      } catch {
        // If MSAL isn't ready, fall back to a hard reload to /login (or root).
        window.location.assign('/');
      }
    };

    const scheduleNext = (ms) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(fireLogout, ms);
    };

    const onActivity = () => {
      lastActivityRef.current = Date.now();
      scheduleNext(timeoutMs);
    };

    scheduleNext(timeoutMs);
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, onActivity));
    };
  }, [isSysAdmin, currentUser, msalInstance, hydrated, getPref]);
}
